import React, { useState, useEffect, useRef } from "react";
import {
    X,
    Save,
    Mail,
    Loader2,
    Server,
    Key,
    Shield,
    User,
    FileJson,
    Lock as LockIcon,
    Globe,
    ChevronDown,
    Check,
    Eye,
    EyeOff,
    Send,
    AlertCircle,
    Settings,
    ArrowLeft
} from "lucide-react";
import integrationapi from "../../../services/integrationapi";
import { showError, showSuccess } from "../../../utils/toast";

interface EmailIntegrationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    integrationData?: any;
}

interface DropdownOption {
    value: string;
    label: string;
    icon?: React.ComponentType<{ className?: string }>;
    badge?: string;
}

interface CustomDropdownProps {
    value: string;
    onChange: (value: string) => void;
    options: DropdownOption[];
    disabled?: boolean;
    label?: string;
}

const CustomDropdown: React.FC<CustomDropdownProps> = ({
    value,
    onChange,
    options,
    disabled = false,
    label
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    const selectedOption = options.find(opt => opt.value === value) || options[0];

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
        <div className="relative" ref={dropdownRef}>
            {label && (
                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1.5 px-0.5">
                    {label}
                </label>
            )}
            <button
                type="button"
                disabled={disabled}
                onClick={() => setIsOpen(!isOpen)}
                className={`w-full bg-[#0D1117] border rounded-xl px-3.5 py-2.5 text-left flex items-center justify-between transition-all outline-none ${
                    isOpen ? 'border-orange-500 ring-1 ring-orange-500/20' : 'border-gray-800 hover:border-gray-700'
                } ${disabled ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer'}`}
            >
                <div className="flex items-center gap-2.5 min-w-0 pr-2">
                    {selectedOption?.icon && (
                        <div className="w-6 h-6 rounded-md bg-gray-800/80 border border-gray-700/60 flex items-center justify-center shrink-0 text-orange-400">
                            <selectedOption.icon className="w-3.5 h-3.5" />
                        </div>
                    )}
                    <span className="text-xs font-semibold text-gray-200 truncate">{selectedOption?.label}</span>
                    {selectedOption?.badge && (
                        <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-orange-500/10 text-orange-400 border border-orange-500/20 shrink-0">
                            {selectedOption.badge}
                        </span>
                    )}
                </div>
                <ChevronDown className={`w-3.5 h-3.5 text-gray-400 transition-transform duration-200 shrink-0 ${isOpen ? 'rotate-180 text-orange-400' : ''}`} />
            </button>

            {isOpen && !disabled && (
                <div className="absolute z-50 left-0 right-0 mt-1.5 bg-[#0E131F] border border-gray-700/80 rounded-xl shadow-2xl py-1 overflow-hidden animate-in fade-in zoom-in-95 duration-150 backdrop-blur-xl">
                    {options.map((opt) => {
                        const isSelected = opt.value === value;
                        const Icon = opt.icon;
                        return (
                            <button
                                key={opt.value}
                                type="button"
                                onClick={() => {
                                    onChange(opt.value);
                                    setIsOpen(false);
                                }}
                                className={`w-full px-3.5 py-2 flex items-center justify-between text-left transition-colors cursor-pointer ${
                                    isSelected
                                        ? 'bg-orange-500/10 text-orange-300'
                                        : 'text-gray-300 hover:bg-gray-800/60 hover:text-white'
                                }`}
                            >
                                <div className="flex items-center gap-2.5 min-w-0 pr-2">
                                    {Icon && (
                                        <div className={`w-6 h-6 rounded-md border flex items-center justify-center shrink-0 ${
                                            isSelected
                                                ? 'bg-orange-500/20 border-orange-500/40 text-orange-400'
                                                : 'bg-gray-800/60 border-gray-700/50 text-gray-400'
                                        }`}>
                                            <Icon className="w-3 h-3" />
                                        </div>
                                    )}
                                    <span className="text-xs font-medium truncate">{opt.label}</span>
                                    {opt.badge && (
                                        <span className="text-[8px] font-bold uppercase tracking-wider px-1 py-0.5 rounded bg-orange-500/15 text-orange-400 border border-orange-500/30 shrink-0">
                                            {opt.badge}
                                        </span>
                                    )}
                                </div>
                                {isSelected && (
                                    <Check className="w-3.5 h-3.5 text-orange-400 shrink-0 ml-2" />
                                )}
                            </button>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const EmailIntegrationModal: React.FC<EmailIntegrationModalProps> = ({
    isOpen,
    onClose,
    onSuccess,
    integrationData
}) => {
    const isEditing = !!integrationData;

    const [activeTab, setActiveTab] = useState<"configuration" | "test">("configuration");
    const [isLoading, setIsLoading] = useState(false);
    const [isTesting, setIsTesting] = useState(false);
    const [testEmail, setTestEmail] = useState("");
    const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);

    const [authMethod, setAuthMethod] = useState("basic");
    const [authProvider, setAuthProvider] = useState("generic");
    const [showSmtpPassword, setShowSmtpPassword] = useState(false);
    const [showClientSecret, setShowClientSecret] = useState(false);
    const [showApiKey, setShowApiKey] = useState(false);

    const [formData, setFormData] = useState({
        sender_email: '',
        smtp_server: '',
        smtp_port: '' as string | number,
        use_ssl: false,
        use_tls: true,
        smtp_username: '',
        smtp_password: '',
        api_key: '',
        client_id: '',
        client_secret: '',
        tenant_id: '',
        admin_email: '',
        service_account_json: '',
        custom_email: true,
    });

    useEffect(() => {
        if (isOpen) {
            setActiveTab("configuration");
            setIsTesting(false);
            setTestResult(null);
            setTestEmail("");

            if (isEditing && integrationData?.integration_data) {
                const data = typeof integrationData.integration_data === 'string'
                    ? JSON.parse(integrationData.integration_data)
                    : integrationData.integration_data;

                const rawMethod = data.auth_method || (data.client_id ? 'microsoft_graph' : 'basic');
                const mappedMethod = (rawMethod === 'client_credentials' || rawMethod === 'office365')
                    ? 'microsoft_graph'
                    : rawMethod;

                setAuthMethod(mappedMethod);
                setAuthProvider(data.auth_provider || (mappedMethod === 'microsoft_graph' ? 'microsoft_graph' : 'generic'));
                setFormData({
                    sender_email: data.sender_email || '',
                    smtp_server: data.smtp_server || '',
                    smtp_port: data.smtp_port !== undefined ? data.smtp_port : '',
                    use_ssl: data.use_ssl || false,
                    use_tls: data.use_tls ?? true,
                    smtp_username: data.smtp_username || '',
                    smtp_password: data.smtp_password || '',
                    api_key: data.api_key || '',
                    client_id: data.client_id || '',
                    client_secret: data.client_secret || '',
                    tenant_id: data.tenant_id || '',
                    admin_email: data.admin_email || '',
                    service_account_json: data.service_account_json || '',
                    custom_email: data.custom_email !== undefined ? data.custom_email : true,
                });
                if (data.sender_email) {
                    setTestEmail(data.sender_email);
                }
            } else {
                setAuthMethod("basic");
                setAuthProvider("generic");
                setFormData({
                    sender_email: '',
                    smtp_server: '',
                    smtp_port: '',
                    use_ssl: false,
                    use_tls: true,
                    smtp_username: '',
                    smtp_password: '',
                    api_key: '',
                    client_id: '',
                    client_secret: '',
                    tenant_id: '',
                    admin_email: '',
                    service_account_json: '',
                    custom_email: true,
                });
            }
            setIsLoading(false);
            setShowSmtpPassword(false);
            setShowClientSecret(false);
            setShowApiKey(false);
        }
    }, [isOpen, isEditing, integrationData]);

    if (!isOpen) return null;

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const handleAuthMethodChange = (method: string) => {
        setAuthMethod(method);
        if (method === 'basic') setAuthProvider('generic');
        else if (method === 'microsoft_graph') setAuthProvider('microsoft_graph');
        else if (method === 'api_key') setAuthProvider('sendgrid');
        else if (method === 'service_account') setAuthProvider('gmail');
    };

    const validateSettings = () => {
        if (!formData.sender_email?.trim()) {
            return "Please enter a sender email address";
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.sender_email.trim())) {
            return "Please enter a valid sender email address (e.g. alerts@company.com)";
        }
        if (authMethod === 'basic') {
            if (!formData.smtp_server?.trim()) return "Please enter SMTP server host";
            if (formData.smtp_port) {
                const port = Number(formData.smtp_port);
                if (isNaN(port) || port < 1 || port > 65535) {
                    return "SMTP port must be a valid number between 1 and 65535";
                }
            }
            if (!formData.smtp_username?.trim()) return "Please enter SMTP username";
            if (!formData.smtp_password?.trim()) return "Please enter SMTP password";
        } else if (authMethod === 'microsoft_graph') {
            if (!formData.tenant_id?.trim()) return "Please enter Tenant ID";
            if (!formData.client_id?.trim()) return "Please enter Client ID";
            if (!formData.client_secret?.trim()) return "Please enter Client Secret";
        } else if (authMethod === 'api_key') {
            if (!formData.api_key?.trim()) return "Please enter API Key";
            if (formData.smtp_port) {
                const port = Number(formData.smtp_port);
                if (isNaN(port) || port < 1 || port > 65535) {
                    return "SMTP port must be a valid number between 1 and 65535";
                }
            }
        } else if (authMethod === 'service_account') {
            if (!formData.admin_email?.trim()) return "Please enter Delegated Sender Email";
            if (!emailRegex.test(formData.admin_email.trim())) {
                return "Please enter a valid Delegated Sender Email address";
            }
            if (!formData.service_account_json?.trim()) return "Please enter Service Account JSON";
            try {
                JSON.parse(formData.service_account_json.trim());
            } catch {
                return "Service Account JSON must be valid JSON format";
            }
        }
        return null;
    };

    const buildSettingsPayload = () => {
        const settings: any = {
            auth_method: authMethod,
            auth_provider: authProvider,
            sender_email: formData.sender_email.trim(),
            custom_email: formData.custom_email,
        };

        if (authMethod === 'basic') {
            settings.smtp_server = formData.smtp_server.trim();
            settings.smtp_port = formData.smtp_port ? Number(formData.smtp_port) : 587;
            settings.use_ssl = formData.use_ssl;
            settings.use_tls = formData.use_tls;
            settings.smtp_username = formData.smtp_username.trim();
            settings.smtp_password = formData.smtp_password;
        } else if (authMethod === 'microsoft_graph') {
            settings.tenant_id = formData.tenant_id.trim();
            settings.client_id = formData.client_id.trim();
            settings.client_secret = formData.client_secret.trim();
        } else if (authMethod === 'api_key') {
            settings.api_key = formData.api_key.trim();
            if (formData.smtp_server?.trim()) {
                settings.smtp_server = formData.smtp_server.trim();
            }
            if (formData.smtp_port) {
                settings.smtp_port = Number(formData.smtp_port);
            }
            if (formData.use_tls !== undefined) {
                settings.use_tls = formData.use_tls;
            }
        } else if (authMethod === 'service_account') {
            settings.admin_email = formData.admin_email.trim();
            settings.service_account_json = formData.service_account_json.trim();
        }

        return settings;
    };

    const handleSwitchToTestTab = () => {
        const valError = validateSettings();
        if (valError) {
            showError(valError);
            return;
        }
        if (!testEmail.trim() && formData.sender_email.trim()) {
            setTestEmail(formData.sender_email.trim());
        }
        setTestResult(null);
        setActiveTab("test");
    };

    const handleSendTestEmail = async () => {
        const valError = validateSettings();
        if (valError) {
            showError(valError);
            setActiveTab("configuration");
            return;
        }

        const targetEmail = testEmail.trim();
        if (!targetEmail) {
            showError("Please enter a recipient email address (to)");
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(targetEmail)) {
            showError("Please enter a valid recipient email address");
            return;
        }

        setIsTesting(true);
        setTestResult(null);

        try {
            const settings = buildSettingsPayload();
            delete settings.custom_email;

            const payload = {
                to: targetEmail,
                settings,
            };

            const response = await integrationapi.post("integrations/smtp/test", payload);

            if (response.data && response.data.status === false) {
                const msg = response.data.message || "Test email delivery failed";
                setTestResult({ success: false, message: msg });
                showError(msg);
            } else {
                const msg = response.data?.message || `Test email sent successfully to ${targetEmail}`;
                setTestResult({ success: true, message: msg });
                showSuccess(msg);
            }
        } catch (error: any) {
            console.error("Email test failed:", error);
            const errMsg = error.response?.data?.message || error.response?.data?.detail || error.message || "Failed to send test email";
            setTestResult({ success: false, message: errMsg });
            showError(errMsg);
        } finally {
            setIsTesting(false);
        }
    };

    const handleSubmit = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();

        const valError = validateSettings();
        if (valError) {
            showError(valError);
            setActiveTab("configuration");
            return;
        }

        setIsLoading(true);
        try {
            const settings = buildSettingsPayload();

            let response;
            try {
                response = await integrationapi.patch("integrations/smtp/settings", settings);
            } catch (patchErr: any) {
                if (patchErr.response?.status === 405 || patchErr.response?.status === 404) {
                    response = await integrationapi.post("integrations/smtp/settings", settings);
                } else {
                    throw patchErr;
                }
            }

            if (response.data?.status !== false) {
                showSuccess(`Email integration ${isEditing ? 'updated' : 'configured'} successfully`);
                onSuccess();
                onClose();
            } else {
                showError(response.data.message || "Operation failed");
            }
        } catch (error: any) {
            console.error("Email connect failed:", error);
            showError(error.response?.data?.message || error.message || "An error occurred");
        } finally {
            setIsLoading(false);
        }
    };

    const authMethodOptions: DropdownOption[] = [
        {
            value: "basic",
            label: "Basic Authentication (SMTP)",
            icon: Server,
        },
        {
            value: "microsoft_graph",
            label: "Microsoft Graph API",
            icon: Globe,
            badge: "Microsoft 365",
        },
        {
            value: "api_key",
            label: "API Key (SendGrid, Mailgun, SMTP2Go)",
            icon: Key,
        },
    ];

    const apiProviderOptions: DropdownOption[] = [
        {
            value: "sendgrid",
            label: "SendGrid",
            icon: Mail,
        },
        {
            value: "mailgun",
            label: "Mailgun",
            icon: Mail,
        },
        {
            value: "smtp2go",
            label: "SMTP2Go",
            icon: Mail,
        },
    ];

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-200"
            onClick={handleBackdropClick}
        >
            <div className="bg-[#111827] border border-gray-700/50 rounded-2xl w-full max-w-lg shadow-2xl animate-in zoom-in-95 duration-200 relative flex flex-col max-h-[90vh] overflow-hidden [color-scheme:dark]">
                {/* Modal Header */}
                <div className="px-6 py-4 border-b border-gray-800 flex items-center justify-between shrink-0 bg-[#111827]">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-xl bg-orange-500/10 border border-orange-500/20 flex items-center justify-center text-orange-400">
                            <Mail className="w-4 h-4" />
                        </div>
                        <div>
                            <h2 className="text-base font-bold text-white">
                                {isEditing ? 'Update Email Integration' : 'Configure Email Integration'}
                            </h2>
                            <p className="text-[11px] text-gray-400">Outgoing mail server & API configuration</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-500 hover:text-white p-1 hover:bg-gray-800 rounded-lg transition-colors cursor-pointer"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* Tabs Header */}
                <div className="flex border-b border-gray-800 bg-[#0D1117] px-6 shrink-0 select-none">
                    <button
                        type="button"
                        onClick={() => activeTab === "test" && setActiveTab("configuration")}
                        disabled={activeTab === "configuration"}
                        className={`flex items-center gap-2 py-2.5 px-3 text-xs font-semibold border-b-2 transition-all ${
                            activeTab === "configuration"
                                ? "border-orange-500 text-orange-400 cursor-default"
                                : "border-transparent text-gray-400 hover:text-gray-200 cursor-pointer"
                        }`}
                    >
                        <Settings className="w-3.5 h-3.5" />
                        Configuration
                    </button>
                    <div
                        className={`flex items-center gap-2 py-2.5 px-3 text-xs font-semibold border-b-2 transition-all select-none ${
                            activeTab === "test"
                                ? "border-orange-500 text-orange-400 cursor-default"
                                : "border-transparent text-gray-500 opacity-50 cursor-not-allowed"
                        }`}
                        title="Click the Test button below to switch to testing"
                    >
                        <Send className="w-3.5 h-3.5" />
                        Test Connection
                    </div>
                </div>

                <form onSubmit={handleSubmit} noValidate className="flex flex-col flex-1 overflow-hidden">
                    <div className="p-5 md:p-6 space-y-4 overflow-y-auto custom-scroll">
                        {/* Read-only warning if not editable */}
                        {integrationData?.editable === false && (
                            <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-xl text-blue-400 text-xs flex items-center gap-2">
                                <Globe className="w-4 h-4 shrink-0" />
                                <span>Global integration settings are read-only.</span>
                            </div>
                        )}

                        {/* ─── TAB 1: CONFIGURATION ─── */}
                        {activeTab === "configuration" && (
                            <div className="space-y-4">
                                <CustomDropdown
                                    label="Authentication Method"
                                    value={authMethod}
                                    onChange={handleAuthMethodChange}
                                    options={authMethodOptions}
                                    disabled={integrationData?.editable === false}
                                />

                                {/* Sender Email */}
                                <div>
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1.5 px-0.5">
                                        Sender Email Address
                                    </label>
                                    <div className="relative group">
                                        <Mail className="absolute left-3.5 top-3 h-3.5 w-3.5 text-gray-500 group-focus-within:text-orange-500 transition-colors" />
                                        <input
                                            type="email"
                                            disabled={integrationData?.editable === false}
                                            value={formData.sender_email}
                                            onChange={(e) => setFormData({ ...formData, sender_email: e.target.value })}
                                            className="w-full bg-[#0D1117] border border-gray-800 rounded-xl pl-9 pr-3 py-2.5 text-xs text-gray-200 focus:border-orange-500/50 outline-none transition-all disabled:opacity-70"
                                            placeholder="sender@company.com"
                                        />
                                    </div>
                                </div>

                                {/* ── Basic Auth Fields ── */}
                                {authMethod === 'basic' && (
                                    <div className="space-y-3 pt-1">
                                        <div className="flex flex-wrap gap-4 p-2.5 bg-[#0D1117] border border-gray-800 rounded-xl">
                                            <label className="flex items-center gap-2 cursor-pointer text-xs text-gray-300 hover:text-white transition-colors">
                                                <input
                                                    type="checkbox"
                                                    disabled={integrationData?.editable === false}
                                                    checked={formData.use_tls}
                                                    onChange={(e) => {
                                                        const checked = e.target.checked;
                                                        setFormData({
                                                            ...formData,
                                                            use_tls: checked,
                                                            use_ssl: checked ? false : formData.use_ssl,
                                                            smtp_port: checked ? 587 : formData.smtp_port
                                                        });
                                                    }}
                                                    className="rounded bg-gray-900 border-gray-700 text-orange-600 focus:ring-orange-500 w-3.5 h-3.5 cursor-pointer"
                                                />
                                                Use TLS (Port 587)
                                            </label>
                                            <label className="flex items-center gap-2 cursor-pointer text-xs text-gray-300 hover:text-white transition-colors">
                                                <input
                                                    type="checkbox"
                                                    disabled={integrationData?.editable === false}
                                                    checked={formData.use_ssl}
                                                    onChange={(e) => {
                                                        const checked = e.target.checked;
                                                        setFormData({
                                                            ...formData,
                                                            use_ssl: checked,
                                                            use_tls: checked ? false : formData.use_tls,
                                                            smtp_port: checked ? 465 : formData.smtp_port
                                                        });
                                                    }}
                                                    className="rounded bg-gray-900 border-gray-700 text-orange-600 focus:ring-orange-500 w-3.5 h-3.5 cursor-pointer"
                                                />
                                                Use SSL (Port 465)
                                            </label>
                                        </div>

                                        <div className="grid grid-cols-3 gap-3">
                                            <div className="col-span-2">
                                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1.5 px-0.5">
                                                    SMTP Server Host
                                                </label>
                                                <div className="relative group">
                                                    <Server className="absolute left-3 top-3 h-3.5 w-3.5 text-gray-500 group-focus-within:text-orange-500 transition-colors" />
                                                    <input
                                                        type="text"
                                                        disabled={integrationData?.editable === false}
                                                        value={formData.smtp_server}
                                                        onChange={(e) => setFormData({ ...formData, smtp_server: e.target.value })}
                                                        className="w-full bg-[#0D1117] border border-gray-800 rounded-xl pl-9 pr-3 py-2.5 text-xs text-gray-200 focus:border-orange-500/50 outline-none transition-all disabled:opacity-70"
                                                        placeholder="smtp.example.com"
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1.5 px-0.5">
                                                    Port
                                                </label>
                                                <input
                                                    type="number"
                                                    disabled={integrationData?.editable === false}
                                                    value={formData.smtp_port}
                                                    onChange={(e) => setFormData({ ...formData, smtp_port: e.target.value ? parseInt(e.target.value) : '' })}
                                                    className="w-full bg-[#0D1117] border border-gray-800 rounded-xl px-3 py-2.5 text-xs text-gray-200 focus:border-orange-500/50 outline-none transition-all disabled:opacity-70"
                                                    placeholder="587"
                                                />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1.5 px-0.5">
                                                    SMTP Username
                                                </label>
                                                <div className="relative group">
                                                    <User className="absolute left-3 top-3 h-3.5 w-3.5 text-gray-500 group-focus-within:text-orange-500 transition-colors" />
                                                    <input
                                                        type="text"
                                                        disabled={integrationData?.editable === false}
                                                        value={formData.smtp_username}
                                                        onChange={(e) => setFormData({ ...formData, smtp_username: e.target.value })}
                                                        className="w-full bg-[#0D1117] border border-gray-800 rounded-xl pl-9 pr-3 py-2.5 text-xs text-gray-200 focus:border-orange-500/50 outline-none transition-all disabled:opacity-70"
                                                        placeholder="username"
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1.5 px-0.5">
                                                    SMTP Password
                                                </label>
                                                <div className="relative group">
                                                    <LockIcon className="absolute left-3 top-3 h-3.5 w-3.5 text-gray-500 group-focus-within:text-orange-500 transition-colors" />
                                                    <input
                                                        type={showSmtpPassword ? "text" : "password"}
                                                        disabled={integrationData?.editable === false}
                                                        value={formData.smtp_password}
                                                        onChange={(e) => setFormData({ ...formData, smtp_password: e.target.value })}
                                                        autoComplete="new-password"
                                                        className="w-full bg-[#0D1117] border border-gray-800 rounded-xl pl-9 pr-8 py-2.5 text-xs text-gray-200 focus:border-orange-500/50 outline-none transition-all disabled:opacity-70"
                                                        placeholder="password"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => setShowSmtpPassword(!showSmtpPassword)}
                                                        className="absolute right-2.5 top-3 text-gray-500 hover:text-gray-300"
                                                    >
                                                        {showSmtpPassword ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* ── Microsoft Graph Fields ── */}
                                {authMethod === 'microsoft_graph' && (
                                    <div className="space-y-3 pt-1">
                                        <div className="grid grid-cols-2 gap-3">
                                            <div>
                                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1.5 px-0.5">
                                                    Tenant ID <span className="text-orange-500">*</span>
                                                </label>
                                                <div className="relative group">
                                                    <Shield className="absolute left-3 top-3 h-3.5 w-3.5 text-gray-500 group-focus-within:text-orange-500 transition-colors" />
                                                    <input
                                                        type="text"
                                                        disabled={integrationData?.editable === false}
                                                        value={formData.tenant_id}
                                                        onChange={(e) => setFormData({ ...formData, tenant_id: e.target.value })}
                                                        className="w-full bg-[#0D1117] border border-gray-800 rounded-xl pl-9 pr-3 py-2.5 text-xs text-gray-200 focus:border-orange-500/50 outline-none font-mono"
                                                        placeholder="Tenant ID"
                                                    />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1.5 px-0.5">
                                                    Client ID <span className="text-orange-500">*</span>
                                                </label>
                                                <div className="relative group">
                                                    <User className="absolute left-3 top-3 h-3.5 w-3.5 text-gray-500 group-focus-within:text-orange-500 transition-colors" />
                                                    <input
                                                        type="text"
                                                        disabled={integrationData?.editable === false}
                                                        value={formData.client_id}
                                                        onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                                                        className="w-full bg-[#0D1117] border border-gray-800 rounded-xl pl-9 pr-3 py-2.5 text-xs text-gray-200 focus:border-orange-500/50 outline-none font-mono"
                                                        placeholder="Client ID"
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        <div>
                                            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1.5 px-0.5">
                                                Client Secret <span className="text-orange-500">*</span>
                                            </label>
                                            <div className="relative group">
                                                <Key className="absolute left-3 top-3 h-3.5 w-3.5 text-gray-500 group-focus-within:text-orange-500 transition-colors" />
                                                <input
                                                    type={showClientSecret ? "text" : "password"}
                                                    disabled={integrationData?.editable === false}
                                                    value={formData.client_secret}
                                                    onChange={(e) => setFormData({ ...formData, client_secret: e.target.value })}
                                                    className="w-full bg-[#0D1117] border border-gray-800 rounded-xl pl-9 pr-8 py-2.5 text-xs text-gray-200 focus:border-orange-500/50 outline-none"
                                                    placeholder="Client Secret"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowClientSecret(!showClientSecret)}
                                                    className="absolute right-2.5 top-3 text-gray-500 hover:text-gray-300"
                                                >
                                                    {showClientSecret ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5 text-gray-400" />}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* ── API Key Fields ── */}
                                {authMethod === 'api_key' && (
                                    <div className="space-y-3 pt-1">
                                        <CustomDropdown
                                            label="API Provider"
                                            value={authProvider}
                                            onChange={(p) => setAuthProvider(p)}
                                            options={apiProviderOptions}
                                            disabled={integrationData?.editable === false}
                                        />
                                        <div>
                                            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1.5 px-0.5">
                                                API Key
                                            </label>
                                            <div className="relative group">
                                                <Key className="absolute left-3 top-3 h-3.5 w-3.5 text-gray-500 group-focus-within:text-orange-500 transition-colors" />
                                                <input
                                                    type={showApiKey ? "text" : "password"}
                                                    disabled={integrationData?.editable === false}
                                                    value={formData.api_key}
                                                    onChange={(e) => setFormData({ ...formData, api_key: e.target.value })}
                                                    className="w-full bg-[#0D1117] border border-gray-800 rounded-xl pl-9 pr-8 py-2.5 text-xs text-gray-200 focus:border-orange-500/50 outline-none"
                                                    placeholder="API Key / Token"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setShowApiKey(!showApiKey)}
                                                    className="absolute right-2.5 top-3 text-gray-500 hover:text-gray-300"
                                                >
                                                    {showApiKey ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* ── Google Workspace Service Account Fields ── */}
                                {authMethod === 'service_account' && (
                                    <div className="space-y-3 pt-1">
                                        <div>
                                            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1.5 px-0.5">
                                                Delegated Sender Email Address
                                            </label>
                                            <div className="relative group">
                                                <User className="absolute left-3 top-3 h-3.5 w-3.5 text-gray-500 group-focus-within:text-orange-500 transition-colors" />
                                                <input
                                                    type="email"
                                                    disabled={integrationData?.editable === false}
                                                    value={formData.admin_email}
                                                    onChange={(e) => setFormData({ ...formData, admin_email: e.target.value })}
                                                    className="w-full bg-[#0D1117] border border-gray-800 rounded-xl pl-9 pr-3 py-2.5 text-xs text-gray-200 focus:border-orange-500/50 outline-none transition-all"
                                                    placeholder="admin@company.com"
                                                />
                                            </div>
                                        </div>
                                        <div>
                                            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1.5 px-0.5">
                                                Service Account JSON
                                            </label>
                                            <div className="relative group">
                                                <FileJson className="absolute left-3 top-3 h-3.5 w-3.5 text-gray-500 group-focus-within:text-orange-500 transition-colors" />
                                                <textarea
                                                    disabled={integrationData?.editable === false}
                                                    value={formData.service_account_json}
                                                    onChange={(e) => setFormData({ ...formData, service_account_json: e.target.value })}
                                                    className="w-full bg-[#0D1117] border border-gray-800 rounded-xl pl-9 pr-3 py-2.5 text-xs text-gray-200 focus:border-orange-500/50 outline-none transition-all font-mono min-h-[90px]"
                                                    placeholder='{"type": "service_account", ...}'
                                                />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Routing Checkbox */}
                                <div className="pt-1">
                                    <label className="flex items-center gap-3 p-3 bg-[#0D1117] border border-gray-800 hover:border-gray-700/80 rounded-xl cursor-pointer transition-all">
                                        <input
                                            type="checkbox"
                                            disabled={integrationData?.editable === false}
                                            checked={formData.custom_email}
                                            onChange={(e) => setFormData({ ...formData, custom_email: e.target.checked })}
                                            className="rounded bg-gray-900 border-gray-700 text-orange-600 focus:ring-orange-500 w-3.5 h-3.5 cursor-pointer"
                                        />
                                        <div>
                                            <div className="text-xs font-semibold text-gray-200">
                                                Enable Email Delivery
                                            </div>
                                            <p className="text-[11px] text-gray-500 mt-0.5">
                                                Use this email address for all Custom Reports automation notifications and alerts.
                                            </p>
                                        </div>
                                    </label>
                                </div>
                            </div>
                        )}

                        {/* ─── TAB 2: TEST CONNECTION ─── */}
                        {activeTab === "test" && (
                            <div className="space-y-4">
                                {/* Preview Configuration Summary */}
                                <div className="p-3.5 bg-[#0D1117] border border-gray-800 rounded-xl flex items-center justify-between text-xs">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-lg bg-orange-500/10 border border-orange-500/20 flex items-center justify-center text-orange-400 shrink-0">
                                            <Mail className="w-4 h-4" />
                                        </div>
                                        <div className="min-w-0">
                                            <div className="text-gray-200 font-semibold truncate">
                                                {authMethod === 'basic' ? 'Basic Authentication (SMTP)' : authMethod === 'microsoft_graph' ? 'Microsoft Graph API' : authMethod === 'api_key' ? `${authProvider.toUpperCase()} API` : 'Google Service Account'}
                                            </div>
                                            <div className="text-[11px] text-gray-400 truncate mt-0.5">
                                                From: {formData.sender_email || 'Not configured'}
                                            </div>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setActiveTab("configuration")}
                                        className="text-xs text-orange-400 hover:text-orange-300 font-medium transition-colors cursor-pointer shrink-0 ml-2"
                                    >
                                        Edit
                                    </button>
                                </div>

                                <div>
                                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1.5 px-0.5">
                                        Recipient Email Address (to)
                                    </label>
                                    <div className="relative group">
                                        <Mail className="absolute left-3.5 top-3 h-3.5 w-3.5 text-gray-500 group-focus-within:text-orange-500 transition-colors" />
                                        <input
                                            type="email"
                                            disabled={isTesting}
                                            value={testEmail}
                                            onChange={(e) => {
                                                setTestEmail(e.target.value);
                                                setTestResult(null);
                                            }}
                                            placeholder="recipient@example.com"
                                            className="w-full bg-[#0D1117] border border-gray-800 rounded-xl pl-9 pr-3 py-2.5 text-xs text-gray-200 focus:border-orange-500/50 outline-none transition-all"
                                            autoFocus
                                        />
                                    </div>
                                </div>

                                <div>
                                    <button
                                        type="button"
                                        disabled={isTesting || !testEmail.trim()}
                                        onClick={handleSendTestEmail}
                                        className="w-full py-2.5 bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/30 text-orange-400 hover:text-orange-300 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                                    >
                                        {isTesting ? <Loader2 className="animate-spin w-3.5 h-3.5" /> : <Send className="w-3.5 h-3.5" />}
                                        {isTesting ? 'Sending Test Email...' : 'Send Test Email'}
                                    </button>
                                </div>

                                {/* Result Status Banner */}
                                {testResult && (
                                    <div className={`p-3 rounded-xl text-xs flex items-start gap-2.5 border ${
                                        testResult.success
                                            ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
                                            : 'bg-red-500/10 border-red-500/30 text-red-300'
                                    }`}>
                                        {testResult.success ? (
                                            <Check className="w-4 h-4 shrink-0 mt-0.5 text-emerald-400" />
                                        ) : (
                                            <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-red-400" />
                                        )}
                                        <span className="leading-relaxed break-words">{testResult.message}</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Modal Footer */}
                    <div className="px-6 py-4 border-t border-gray-800 flex justify-end items-center gap-3 shrink-0 bg-[#111827]">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-5 py-2.5 border border-gray-700 rounded-xl text-xs font-semibold text-gray-400 hover:bg-gray-800 transition-all cursor-pointer"
                        >
                            Cancel
                        </button>

                        {activeTab === "configuration" ? (
                            <button
                                type="button"
                                onClick={handleSwitchToTestTab}
                                disabled={isLoading || isTesting || integrationData?.editable === false}
                                className="px-5 py-2.5 border border-orange-500/30 hover:border-orange-500/60 bg-orange-500/10 hover:bg-orange-500/20 text-orange-400 hover:text-orange-300 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer disabled:opacity-50"
                            >
                                <Send className="w-3.5 h-3.5" />
                                Test
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={() => setActiveTab("configuration")}
                                className="px-5 py-2.5 border border-gray-700 rounded-xl text-xs font-semibold text-gray-300 hover:bg-gray-800 transition-all flex items-center gap-1.5 cursor-pointer"
                            >
                                <ArrowLeft className="w-3.5 h-3.5" />
                                Back
                            </button>
                        )}

                        <button
                            type="submit"
                            disabled={isLoading || isTesting || integrationData?.editable === false}
                            className="px-5 py-2.5 bg-orange-600 hover:bg-orange-500 text-white rounded-xl text-xs font-bold shadow-lg shadow-orange-900/20 disabled:opacity-50 transition-all flex justify-center items-center gap-2 cursor-pointer"
                        >
                            {isLoading ? <Loader2 className="animate-spin w-3.5 h-3.5" /> : <Save className="w-3.5 h-3.5" />}
                            {isEditing ? 'Save Changes' : 'Connect Service'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EmailIntegrationModal;
